<template>
  <div id="base2bot" class="box">
    <vue-loading :active.sync="loading" :is-full-page="true" loader="dots" color="#70A1F0"></vue-loading>
    <div class="row">
      <div class="column">
        <div class="row">
          <div class="column">
            <div>
              <transition name="fade" appear>
                <div class="column">
                  <MetricGraph :metrics='metrics'
                    :name='"Numbers"'
                    :date='date'
                    :period='period'
                    v-on:updateMetric='updateMetric' />
                </div>
              </transition>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="column">

            <transition name="fade" appear>
              <div v-if="report.users && report.users.length > 0">
                <div class="card">
                  <h4>Humans<span class="user-count">{{ report.users.length }}</span></h4>
                  <dl>
                    <template v-for="user in report['users']">
                      <dt :key=user.user>{{ user.user }}</dt><dd :key="user.user + user.count">{{ user.count }}</dd>
                    </template>
                  </dl>
                </div>
              </div>
            </transition>

            <transition name="fade" appear>
              <div v-if="report.contracts && report.contracts.length > 0">
                <div class="card">
                  <h4 class="teams">Teams<span class="contract-count">{{ report.contracts.length }}</span></h4>
                  <dl>
                    <template v-for="contract in report['contracts']">
                      <dt @mouseover="hoverContract = contract.contract"
                          @mouseout="hoverContract = null"
                          class="contracts"
                          v-bind:class="[
                            {'hover-contract': (hoverContract == contract.contract)},
                            {'inactive-contract': (hoverContract != contract.contract)}
                          ]"
                          v-on:click="updateContract(contract.contract)"
                          :key=contract.contract>{{ contract.contract }}</dt>
                        <dd :key="contract.contract + contract.count.toString()">{{ contract.count }}</dd>
                    </template>
                  </dl>
                </div>
              </div>
            </transition>

          </div>
          <div class="column">

            <transition name="fade" appear>
              <div v-if="report.messages.length > 0">
                <div class="card" v-bind:class="[ {positive: (report.sentiment > sentimentThreshold)}, {negative: (report.sentiment < sentimentThreshold)} ]">
                  <h4>Responsiveness</h4>
                  <dl>
                    <dt>MTTA</dt><dd>{{ formatTime(report.mtta) }}</dd>
                  </dl>
                  <span class="spacer"></span>
                  <h4>Sentiment</h4>
                  <dl>
                    <dt>Score</dt><dd>{{ report.sentiment.toFixed(2) }}</dd>
                  </dl>
                  <span class="spacer"></span>
                  <h4 v-if="report.topics.length > 0">Words</h4>
                  <dl>
                    <template v-for="topic in report['topics']">
                      <dt :key=topic.topic>{{ topic.topic }}</dt><dd :key="topic.topic + topic.count">{{ topic.count }}</dd>
                    </template>
                  </dl>
                </div>
              </div>
            </transition>

          </div>
        </div>
      </div>
      <div class="column">

        <transition-group name="list" appear>
          <div v-for="message in report['messages']" :key=message.timestamp>
            <div class="card" v-bind:class="[{positive: sentimentPositive(message.sentiment)}, {negative: sentimentNegative(message.sentiment)}, {neutral: sentimentNeutral(message.sentiment)}]">
              <h5>{{ message.user }}<span class="timestamp">{{ formatTimestamp(message.timestamp, period) }}</span></h5>
              <span>{{ message.message }}</span>
              <div v-if="message.ack_ts > 0" class="ack">{{ calcAckTime(message.timestamp, message.ack_ts) }}</div>
            </div>
          </div>
        </transition-group>

      </div>
    </div>
  </div>
</template>
<script>
import HTTP from '@/common/http'
import MetricGraph from '@/components/MetricGraph'
import VueLoading from 'vue-loading-overlay'
import { mapGetters } from 'vuex'

export default {
  components: {
    MetricGraph,
    VueLoading
  },
  props: [
    'id'
  ],
  created: function () {
    this.getMetrics(true)
  },
  computed: {
    ...mapGetters([
      'activeType'
    ])
  },
  watch: {
    id: function () {
      this.report = {
        month: null,
        messages: [],
        topics: [],
        users: null,
        contracts: null,
        sentiment: 0,
        mtta: 0
      }
      this.getMetrics(false)
      this.getMessages()
      this.hoverContract = null
    },
    period: function () {
      this.report = {
        month: null,
        messages: [],
        topics: [],
        users: null,
        contracts: null,
        sentiment: 0,
        mtta: 0
      }
      this.getMetrics(true)
    },
    date: function () {
      this.report = {
        month: null,
        messages: [],
        topics: [],
        users: null,
        contracts: null,
        sentiment: 0,
        mtta: 0
      }
      this.getMessages()
    }
  },
  data () {
    return {
      metrics: [],
      report: {
        month: null,
        messages: [],
        topics: [],
        users: null,
        contracts: null,
        sentiment: 0
      },
      date: null,
      period: 'week',
      sentimentThreshold: 80,
      hoverContract: null,
      loading: false
    }
  },
  methods: {
    updateMetric: function (event) {
      switch (event.eventType) {
        case 'date':
          this.date = event.data.date
          break

        case 'period':
          this.period = event.data.period
          break

        default:
          console.log(event)
          break
      }
    },
    sentimentPositive: function (sentiment) {
      return sentiment === 'POSITIVE'
    },
    sentimentNegative: function (sentiment) {
      return sentiment === 'NEGATIVE'
    },
    sentimentNeutral: function (sentiment) {
      return sentiment === 'NEUTRAL'
    },
    formatTimestamp: function (timestamp, period) {
      let date = new Date(timestamp * 1000)
      let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Octr', 'Nov', 'Dec']
      let weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      let day = date.getDate()
      let weekday = weekdays[date.getDay()]
      let month = months[date.getMonth()]
      let hours = date.getHours()
      let minutes = '0' + date.getMinutes()
      if (period === 'week') {
        return weekday + ' ' + hours + ':' + minutes.substr(-2)
      } else {
        return month + ' ' + day + ', ' + hours + ':' + minutes.substr(-2)
      }
    },
    calcAckTime: function (timestamp, ack) {
      if (ack > 0) {
        let time = ack - timestamp
        return this.formatTime(time)
      } else {
        return ''
      }
    },
    formatTime: function (time) {
      if (time > 0) {
        let seconds = time
        let minutes = Math.floor(seconds / 60)
        let hours = Math.floor(minutes / 60)
        if (hours > 0) {
          return hours + 'h'
        } else if (minutes > 0) {
          return minutes + 'm'
        } else {
          return seconds + 's'
        }
      } else {
        return ''
      }
    },
    getMetrics: function (setDate) {
      this.loading = true
      HTTP.get(`/base2bot/metrics/${this.activeType}/${this.id}/${this.period}`)
        .then((response) => {
          this.metrics = response.data['metrics']
          if (setDate) {
            this.date = response.data['metrics'][11]['date']
          }
        })
        .catch((error) => {
          var msg = (error.response) ? error.response.data.error : error.message
          this.flashMessage.error({
            message: `Failed to get base2bot metrics : ${msg}`
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    getMessages: function () {
      this.loading = true
      HTTP.get(`/base2bot/messages/${this.activeType}/${this.id}/${this.period}/${this.date}`)
        .then((response) => {
          this.report = response.data
        })
        .catch((error) => {
          var msg = (error.response) ? error.response.data.error : error.message
          this.flashMessage.error({
            message: `Failed to get base2bot messages : ${msg}`
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    updateContract: function (contract) {
      this.$store.dispatch('SET_ACTIVE', contract)
    }
  }
}
</script>

<style scoped src='../assets/stylesheet/base2-bot.css'></style>



// WEBPACK FOOTER //
// src/views/Base2Bot.vue